import React from 'react';
import { makeStyles } from 'design-system/styles';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { H3, H2 } from 'design-system/Typography';
import LookupObjectLink from 'corp/components/Lookup/LookupObjectLink';
import { useAsync } from 'react-use-v13';
import AdvancedFilter from 'corp/components/Lookup/sections/ProjectQuickGrab/AdvancedFilter';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import classNames from 'classnames';

interface SectionData {
  value?: SectionValue;
  loading: boolean;
  error?: Error;
}

type ObjectWithId = { _id: string };

interface SectionValue {
  res: ObjectWithId[];
  title: string;
}

const QuickGrab: React.FC<{
  projectId: string;
  usesPipelineV3?: boolean;
}> = ({ projectId, usesPipelineV3 }) => {
  const classes = useStyles();
  const { isRemoAdmin } = useRemoAdminCtx();

  const recentlyCompletedSubtasks: SectionData = useAsync(async () => {
    const res = await apiFetch(null, `corp-api/lookup/${projectId}/recentCompletedSubtasks`, {
      method: 'GET',
    });

    return {
      title: 'Recently Completed Subtasks',
      res,
    };
  }, [projectId]);

  const recentlyCompletedTasks: SectionData = useAsync(async () => {
    const res = await apiFetch(null, `corp-api/lookup/${projectId}/recentCompletedTasks`, {
      method: 'GET',
    });

    return {
      title: 'Recently Completed Tasks',
      res,
    };
  }, [projectId]);

  const recentlyPendingSubtasks: SectionData = useAsync(async () => {
    const res = await apiFetch(null, `corp-api/lookup/${projectId}/recentPendingSubtasks`, {
      method: 'GET',
    });

    return {
      title: 'Recently Pending Subtasks',
      res,
    };
  }, [projectId]);

  const recentlyPendingTasks: SectionData = useAsync(async () => {
    const res = await apiFetch(null, `corp-api/lookup/${projectId}/recentPendingTasks`, {
      method: 'GET',
    });

    return {
      title: 'Recently Pending Tasks',
      res,
    };
  }, [projectId]);

  const recentlySubmittedTrainingAttempts: SectionData = useAsync(async () => {
    const res = await apiFetch(
      null,
      `corp-api/lookup/${projectId}/recentSubmittedTrainingAttempts`,
      {
        method: 'GET',
      },
    );

    return {
      title: 'Recently Submitted Training Attempts',
      res,
    };
  }, [projectId]);

  const sections: SectionData[] = usesPipelineV3
    ? [recentlyCompletedTasks, recentlyPendingTasks, recentlySubmittedTrainingAttempts]
    : [
        recentlyCompletedSubtasks,
        recentlyCompletedTasks,
        recentlyPendingSubtasks,
        recentlyPendingTasks,
        recentlySubmittedTrainingAttempts,
      ];

  const renderSectionData = (section: SectionData) => {
    if (section.loading) {
      return null;
    }

    if (section.value?.res.length) {
      return section.value.res.map((object: ObjectWithId) => (
        <div key={object._id}>
          <LookupObjectLink objectId={object._id} />
        </div>
      ));
    }

    return <div className={classes.noResults}>No Results Found</div>;
  };

  return (
    <div>
      <H2>Quickgrab</H2>
      <div
        className={classNames({
          [classes.grid]: !isRemoAdmin,
          [classes.remoAdminGrid]: isRemoAdmin,
        })}
      >
        {sections.map((section: SectionData, i: number) => {
          return (
            !section.error && (
              <div key={i} className={classes.group}>
                <div className={classes.groupHeader}>
                  <H3>{section.value?.title}</H3>
                </div>
                {renderSectionData(section)}
              </div>
            )
          );
        })}
      </div>
      {!usesPipelineV3 && (
        <div className={classes.group}>
          <div className={classes.groupHeader}>
            <H3>Custom Filter</H3>
          </div>

          <AdvancedFilter projectId={projectId} />
        </div>
      )}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridGap: 16,
    marginTop: 16,
    marginBottom: 16,
  },
  remoAdminGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 16,
    marginTop: 16,
  },
  group: {
    background: theme.colors.gray01,
    borderRadius: 8,
    padding: '16px 24px',
  },
  groupHeader: {
    marginBottom: 8,
  },
  noResults: {
    height: 100,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export default QuickGrab;
